import { m } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Card, Stack, Link, Container, Typography } from '@mui/material';
// routes
import { PATH } from '../../routes/paths';
// hooks
import useLocales from '../../hooks/useLocales';
import useResponsive from '../../hooks/useResponsive';
// layouts
import LanguagePopover from '../../layouts/dashboard/header/LanguagePopover';
// components
import Page from '../../components-custom/Page';
import Logo from '../../components-custom/Logo';
import { MotionContainer, varBounce } from '../../components/animate';
// sections
import LoginForm from '../../sections/auth/LoginForm';
import { SSOIcons } from '../../sections/auth/SSOForm';
// assets
import { IllustrationLogin } from '../../assets/illustrations';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function Login() {
  const { translate } = useLocales();

  const mdUp = useResponsive('up', 'md');

  return (
    <Page title={translate('pageTitle', { page: translate('auth.login.pageName') })}>
      <RootStyle>
        <HeaderStyle>
          <Logo />
          <LanguagePopover />
        </HeaderStyle>

        {mdUp && (
          <SectionStyle component={MotionContainer}>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              {translate('auth.login.baner')}
            </Typography>
            <m.div variants={varBounce().in}>
              <IllustrationLogin sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
            </m.div>
          </SectionStyle>
        )}

        <Container maxWidth="sm">
          <ContentStyle>
            <Stack direction="row" alignItems="center" sx={{ mb: 5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {translate('auth.login.signIn')}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>{translate('auth.login.signInSubtitle')}</Typography>
              </Box>
            </Stack>

            <LoginForm />
            <SSOIcons />

            {!window.isNativeApp && (
              <Typography variant="subtitle1" align="center" sx={{ mt: 3 }}>
                {translate('auth.login.noAccount')}{' '}
                <Link variant="subtitle1" component={RouterLink} to={PATH.auth.register}>
                  {translate('auth.login.getStarted')}
                </Link>
              </Typography>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
